import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Save, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/Kpi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY_ITEM = { category: "", name: "", rate: 0, type: "Veg", active: true };

const ItemDialog = ({ open, value, onClose, onSaved }) => {
  const [f, setF] = useState(value);
  useEffect(() => setF(value), [value]);
  if (!f) return null;
  const save = async () => {
    try {
      const body = { ...f, rate: Number(f.rate) || 0 };
      if (f.id) await api.put(`/menu/${f.id}`, body); else await api.post("/menu", body);
      toast.success("Menu item saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="menu-item-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit item" : "New menu item"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          {[["category", "Category"], ["name", "Item name"], ["rate", "Rate (₹)"], ["type", "Type (Veg / Egg)"]].map(([k, l]) => (
            <div key={k} className={`space-y-1.5 ${k === "name" ? "col-span-2" : ""}`}>
              <Label>{l}</Label>
              <Input value={f[k]} type={k === "rate" ? "number" : "text"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`menu-item-${k}-input`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.name || !f.category} className="bg-amber-600 hover:bg-amber-700" data-testid="menu-item-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const HeadAdder = ({ onAdded }) => {
  const [f, setF] = useState({ category: "Raw Material", name: "", kind: "variable" });
  const add = async () => {
    try { await api.post("/expense-heads", { ...f, category: f.kind === "fixed" ? "Fixed" : f.category }); setF({ ...f, name: "" }); toast.success("Expense head added"); onAdded(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-4">
      <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="head-kind-select">
        <option value="variable">Daily / variable</option><option value="fixed">Monthly fixed</option>
      </select>
      {f.kind === "variable" && <Input className="w-36 h-9" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Category" data-testid="head-category-input" />}
      <Input className="flex-1 min-w-[140px] h-9" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Head name" data-testid="head-name-input" />
      <Button size="sm" onClick={add} disabled={!f.name} data-testid="head-add-button"><Plus className="h-4 w-4" /></Button>
    </div>
  );
};

const PayersPanel = () => {
  const [payers, setPayers] = useState([]);
  const [name, setName] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setPayers(r.data.payers || [])); }, []);
  const save = async (list) => {
    try { const r = await api.put("/settings", { payers: list }); setPayers(r.data.payers); toast.success("Payers updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Who pays expenses" testId="payers-panel">
      <p className="text-sm text-slate-500 mb-3">Names shown in the "Paid by" picker on Daily Entry — e.g. Sagar paid, Jitender paid.</p>
      {payers.map((p) => (
        <div key={p} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-100" data-testid={`payer-row-${p}`}>
          <span className="text-slate-700">{p}</span>
          <button onClick={() => window.confirm(`Remove payer "${p}"? Past entries keep the name.`) && save(payers.filter((x) => x !== p))} className="text-slate-300 hover:text-rose-600" data-testid={`delete-payer-${p}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <div className="flex gap-2 mt-4">
        <Input className="flex-1 h-9" value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a name (e.g. Sagar)" data-testid="payer-name-input"
          onKeyDown={(e) => e.key === "Enter" && name.trim() && (save([...payers, name.trim()]), setName(""))} />
        <Button size="sm" onClick={() => { save([...payers, name.trim()]); setName(""); }} disabled={!name.trim()} data-testid="payer-add-button"><Plus className="h-4 w-4" /></Button>
      </div>
    </Panel>
  );
};

const PasswordPanel = () => {
  const { user } = useAuth();
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.new_password !== f.confirm) return toast.error("New password and confirm do not match");
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      localStorage.setItem("manjap_token", r.data.token);
      setF({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed — use the new one next time you sign in");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Change password" testId="password-panel">
      <p className="text-sm text-slate-500 mb-3">Signed in as <span className="font-medium text-slate-700">{user?.email}</span>. Minimum 8 characters.</p>
      <div className="space-y-3">
        {[["current_password", "Current password"], ["new_password", "New password"], ["confirm", "Confirm new password"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs">{l}</Label>
            <Input type="password" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`password-${k.replace("_", "-")}-input`} />
          </div>
        ))}
        <Button onClick={save} disabled={busy || !f.current_password || f.new_password.length < 8} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="change-password-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />}Update password
        </Button>
      </div>
    </Panel>
  );
};

export default function MenuSettings() {
  const [items, setItems] = useState([]);
  const [heads, setHeads] = useState([]);
  const [ded, setDed] = useState("");
  const [edit, setEdit] = useState(null);

  const load = () => {
    api.get("/menu").then((r) => setItems(r.data.filter((x) => x.active !== false)));
    api.get("/expense-heads").then((r) => setHeads(r.data));
  };
  useEffect(() => { load(); api.get("/settings").then((r) => setDed(r.data.deduction_pct)); }, []);

  const saveDed = async () => {
    try { await api.put("/settings", { deduction_pct: Number(ded) }); toast.success("Deduction updated — all reports recalculate"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delItem = async (it) => {
    if (!window.confirm(`Remove "${it.name}" from the menu?`)) return;
    const r = await api.delete(`/menu/${it.id}`);
    toast.success(r.data.archived ? "Item archived (past sales kept)" : "Item deleted"); load();
  };
  const delHead = async (h) => {
    if (!window.confirm(`Delete expense head "${h.name}"?`)) return;
    await api.delete(`/expense-heads/${h.id}`); load();
  };

  return (
    <div>
      <PageHeader title="Menu & Settings" subtitle="Change a rate here and every day, week and month recalculates." />
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <Panel title={`Menu & rates (${items.length} items)`} className="xl:col-span-2" testId="menu-panel"
          right={<Button size="sm" onClick={() => setEdit({ ...EMPTY_ITEM })} className="bg-amber-600 hover:bg-amber-700" data-testid="add-menu-item-button"><Plus className="h-4 w-4 mr-1" />Add item</Button>}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="menu-table">
              <thead className="border-b"><tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="py-2 pr-3">Category</th><th className="py-2 pr-3">Item</th><th className="py-2 pr-3">Type</th><th className="py-2 pr-3 text-right">Rate</th><th /></tr></thead>
              <tbody>{items.map((it) => (
                <tr key={it.id} className="border-b border-slate-100 hover:bg-amber-50/30">
                  <td className="py-2 pr-3 text-slate-500">{it.category}</td><td className="py-2 pr-3 font-medium text-slate-800">{it.name}</td>
                  <td className="py-2 pr-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${it.type === "Egg" ? "bg-yellow-100 text-yellow-800" : "bg-emerald-50 text-emerald-700"}`}>{it.type}</span></td>
                  <td className="py-2 pr-3 text-right num font-semibold">₹{it.rate}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button onClick={() => setEdit(it)} className="p-1.5 text-slate-400 hover:text-amber-700" data-testid={`edit-item-${it.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => delItem(it)} className="p-1.5 text-slate-400 hover:text-rose-600" data-testid={`delete-item-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>))}</tbody>
            </table>
          </div>
        </Panel>
        <div className="space-y-6">
          <Panel title="Global deduction" testId="deduction-panel">
            <p className="text-sm text-slate-500 mb-3">Deducted from gross sales on every report.</p>
            <div className="flex gap-2">
              <div className="relative flex-1"><Input type="number" value={ded} onChange={(e) => setDed(e.target.value)} className="pr-8 num" data-testid="deduction-pct-input" /><span className="absolute right-3 top-2 text-slate-400">%</span></div>
              <Button onClick={saveDed} data-testid="deduction-save-button"><Save className="h-4 w-4" /></Button>
            </div>
          </Panel>
          <PayersPanel />
          <PasswordPanel />
          <Panel title="Expense heads" testId="expense-heads-panel">
            {["variable", "fixed"].map((k) => (
              <div key={k} className="mb-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">{k === "fixed" ? "Monthly fixed" : "Daily / variable"}</div>
                {heads.filter((h) => h.kind === k && h.active !== false).map((h) => (
                  <div key={h.id} className="flex items-center justify-between py-1.5 text-sm border-b border-slate-100">
                    <span className="text-slate-700">{h.name}</span>
                    <button onClick={() => delHead(h)} className="text-slate-300 hover:text-rose-600" data-testid={`delete-head-${h.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            ))}
            <HeadAdder onAdded={load} />
          </Panel>
        </div>
      </div>
      <ItemDialog open={!!edit} value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
    </div>
  );
}
